import { LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'

function CategoriaProductoModal({ categoria, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(categoria)

  const [nombre, setNombre] = useState(categoria?.nombre || '')
  const [descripcion, setDescripcion] = useState(
    categoria?.descripcion || ''
  )
  const [activo, setActivo] = useState(categoria?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarCategoria = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const nombreLimpio = nombre.trim()

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre de la categoría.')
      return
    }

    try {
      setGuardando(true)

      let categoriaGuardada = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('categorias_producto')
          .update({
            nombre: nombreLimpio,
            descripcion: descripcion.trim() || null,
            activo,
          })
          .eq('id', categoria.id)
          .eq('empresa_id', empresaActual.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        categoriaGuardada = data
      } else {
        const { data, error: errorInsertar } = await supabase
          .from('categorias_producto')
          .insert({
            empresa_id: empresaActual.id,
            nombre: nombreLimpio,
            descripcion: descripcion.trim() || null,
            activo: true,
          })
          .select()
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        categoriaGuardada = data
      }

      onGuardado?.(categoriaGuardada)
    } catch (errorGuardado) {
      console.error(
        'Error al guardar categoría de producto:',
        errorGuardado
      )

      if (errorGuardado.code === '23505') {
        setError(
          'Ya existe una categoría con ese nombre en esta empresa.'
        )
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar la categoría.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion
              ? 'Editar categoría de producto'
              : 'Nueva categoría de producto'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={guardarCategoria}
        >
          <div>
            <label
              htmlFor="nombre"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Nombre
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="nombre"
              type="text"
              value={nombre}
              onChange={(event) => setNombre(event.target.value)}
              placeholder="Ejemplo: Envases"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          <div>
            <label
              htmlFor="descripcion"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Descripción
            </label>

            <textarea
              id="descripcion"
              value={descripcion}
              onChange={(event) =>
                setDescripcion(event.target.value)
              }
              rows={3}
              placeholder="Información adicional de la categoría"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          {esEdicion && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) =>
                  setActivo(event.target.checked)
                }
                disabled={guardando}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Categoría activa
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default CategoriaProductoModal
